import { type PrioritizationReportModel } from 'Pages/Reports/PrioritizationReport.types'

// Dynamic imports to handle pdfmake's type issues
// eslint-disable-next-line @typescript-eslint/no-var-requires
const pdfMake = require('pdfmake/build/pdfmake')
// eslint-disable-next-line @typescript-eslint/no-var-requires
const pdfFonts = require('pdfmake/build/vfs_fonts')

// Type definitions for pdfmake (since the module doesn't export them properly)
type Content = any
type TDocumentDefinitions = any

// Initialize pdfMake with fonts
pdfMake.vfs = pdfFonts.pdfMake.vfs

interface PDFMetadata {
  isdName: string
  timespanName: string
  groupByFacility: boolean
}

/**
 * Generate and download Prioritization Report PDF
 */
export function generatePrioritizationPDF (
  reportData: PrioritizationReportModel[],
  metadata: PDFMetadata
): void {
  if (reportData.length === 0) {
    console.warn('No data to generate PDF')
    return
  }

  const docDefinition = createDocumentDefinition(reportData, metadata)
  pdfMake.createPdf(docDefinition).download(generateFilename(metadata.timespanName))
}

/**
 * Generate PDF filename
 */
function generateFilename (timespanName: string): string {
  const date = new Date().toISOString().split('T')[0]
  const cleanTimespan = timespanName.replace(/[^a-zA-Z0-9]/g, '_')
  return `Prioritization_Report_${cleanTimespan}_${date}.pdf`
}

/**
 * Format date for display
 */
function formatDate (dateString: string | null): string {
  if (dateString == null) return 'N/A'
  const date = new Date(dateString)
  return date.toLocaleDateString()
}

/**
 * Calculate age from DOB
 */
function calculateAge (dobString: string): number {
  const dob = new Date(dobString)
  const today = new Date()
  let age = today.getFullYear() - dob.getFullYear()
  const monthDiff = today.getMonth() - dob.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--
  }
  return age
}

/**
 * Extract first facility from priority locations
 */
function getFirstFacility (priorityLocations: string | null): string {
  if (priorityLocations == null || priorityLocations.length === 0) return 'Unknown Facility'
  const firstLocation = priorityLocations.split(', ')[0]
  // Format is "Provider Name - Facility Name", return the full string as facility identifier
  return firstLocation
}

/**
 * Group children by their first choice facility
 */
function groupByFacility (reportData: PrioritizationReportModel[]): Record<string, PrioritizationReportModel[]> {
  return reportData.reduce<Record<string, PrioritizationReportModel[]>>((acc, child) => {
    const firstFacility = getFirstFacility(child.priorityLocations)
    if (acc[firstFacility] == null) {
      acc[firstFacility] = []
    }
    acc[firstFacility].push(child)
    return acc
  }, {})
}

/**
 * Create the complete PDF document definition
 */
function createDocumentDefinition (
  reportData: PrioritizationReportModel[],
  metadata: PDFMetadata
): TDocumentDefinitions {
  const content: Content[] = []

  // Title
  content.push({
    text: 'Prioritization Report',
    style: 'title',
    alignment: 'center',
    margin: [0, 0, 0, 15]
  })

  // Metadata cards
  content.push({
    columns: [
      {
        stack: [
          { text: 'ISD', style: 'metadataLabel' },
          { text: metadata.isdName, style: 'metadataValue' }
        ],
        fillColor: '#FAFAFA',
        margin: [5, 5, 5, 5]
      },
      {
        stack: [
          { text: 'School Year', style: 'metadataLabel' },
          { text: metadata.timespanName, style: 'metadataValue' }
        ],
        fillColor: '#FAFAFA',
        margin: [5, 5, 5, 5]
      },
      {
        stack: [
          { text: 'Generated', style: 'metadataLabel' },
          { text: new Date().toLocaleDateString(), style: 'metadataValue' }
        ],
        fillColor: '#FAFAFA',
        margin: [5, 5, 5, 5]
      },
      {
        stack: [
          { text: 'Total Children', style: 'metadataLabel' },
          { text: String(reportData.length), style: 'metadataValue' }
        ],
        fillColor: '#FAFAFA',
        margin: [5, 5, 5, 5]
      }
    ],
    columnGap: 5,
    margin: [0, 0, 0, 15]
  })

  // Group data by facility (if enabled)
  const groupedData = metadata.groupByFacility
    ? groupByFacility(reportData)
    : { 'All Children': reportData }
  const sortedFacilities = Object.keys(groupedData).sort()

  // Add card-based layout for each facility
  sortedFacilities.forEach((facilityName, facilityIdx) => {
    const facilityChildren = groupedData[facilityName]
    const capacity = facilityChildren[0]?.providerTotalCapacity
    const activeSeats = facilityChildren[0]?.providerActiveSeats ?? 0
    const availableSeats = (capacity ?? 0) - activeSeats

    // Facility header with badges (only show if grouping is enabled)
    if (metadata.groupByFacility) {
      const headerColumns: any[] = [
        {
          text: facilityName,
          style: 'facilityHeaderText',
          fillColor: '#4DA4A4',
          color: 'white',
          margin: [4, 3, 4, 3],
          width: 'auto'
        },
        {
          text: `${facilityChildren.length} ${facilityChildren.length === 1 ? 'child' : 'children'}`,
          style: 'badge',
          fillColor: '#E3F2FD',
          margin: [4, 3, 4, 3],
          width: 'auto'
        }
      ]

      if (capacity != null) {
        headerColumns.push(
          {
            text: `Capacity: ${capacity}`,
            style: 'badge',
            fillColor: '#F5F5F5',
            margin: [4, 3, 4, 3],
            width: 'auto'
          },
          {
            text: `Active: ${activeSeats}`,
            style: 'badge',
            fillColor: '#E8F5E9',
            margin: [4, 3, 4, 3],
            width: 'auto'
          },
          {
            text: `Available: ${availableSeats}`,
            style: 'badge',
            fillColor: '#FFF3E0',
            margin: [4, 3, 4, 3],
            width: 'auto'
          }
        )
      }

      content.push({
        columns: headerColumns,
        columnGap: 5,
        margin: facilityIdx === 0 ? [0, 0, 0, 8] : [0, 12, 0, 8]
      })
    }

    // Children cards for this facility
    facilityChildren.forEach((child, index) => {
      // Get other priority locations (exclude first one if grouped, show all if not)
      const locations = metadata.groupByFacility
        ? child.priorityLocations?.split(', ').slice(1).join(', ') ?? ''
        : child.priorityLocations ?? ''

      // Create priority factors badges
      const priorityBadges: any[] = []
      if (child.iep) {
        priorityBadges.push({
          text: 'IEP',
          style: 'priorityBadge',
          fillColor: '#E7F3F2',
          color: '#4DA4A4',
          margin: [3, 2, 3, 2]
        })
      }
      if (child.foster) {
        priorityBadges.push({
          text: 'Foster',
          style: 'priorityBadge',
          fillColor: '#E7F3F2',
          color: '#4DA4A4',
          margin: [3, 2, 3, 2]
        })
      }
      if (child.homeless) {
        priorityBadges.push({
          text: 'Homeless',
          style: 'priorityBadge',
          fillColor: '#FFF3E0',
          color: '#FF9800',
          margin: [3, 2, 3, 2]
        })
      }
      if (child.snap) {
        priorityBadges.push({
          text: 'SNAP',
          style: 'priorityBadge',
          fillColor: '#E7F3F2',
          color: '#4DA4A4',
          margin: [3, 2, 3, 2]
        })
      }

      // Child card (wrapped in table for border effect)
      const cardContent: any[] = [
        // Top row: rank, name, age, score, FPL
        {
          columns: [
            {
              width: '*',
              stack: [
                {
                  text: [
                    { text: `#${index + 1} `, style: 'rankNumber' },
                    { text: child.childName, style: 'childName' }
                  ]
                },
                {
                  text: `Age: ${calculateAge(child.dob)} | App Date: ${formatDate(child.appDate)}`,
                  style: 'childDetails',
                  margin: [0, 2, 0, 0]
                }
              ]
            },
            {
              width: 'auto',
              columns: [
                {
                  stack: [
                    { text: 'Score', style: 'metricLabel', alignment: 'center' },
                    { text: String(child.priorityScore), style: 'metricValue', alignment: 'center', color: '#4DA4A4' }
                  ],
                  fillColor: '#E3F2FD',
                  margin: [4, 3, 4, 3],
                  width: 35
                },
                {
                  stack: [
                    { text: 'FPL', style: 'metricLabel', alignment: 'center' },
                    { text: child.fpl != null ? `${child.fpl}%` : 'N/A', style: 'metricValue', alignment: 'center' }
                  ],
                  fillColor: '#FAFAFA',
                  margin: [4, 3, 4, 3],
                  width: 35
                }
              ],
              columnGap: 3
            }
          ],
          columnGap: 8
        }
      ]

      // Priority factors badges
      if (priorityBadges.length > 0) {
        cardContent.push({
          columns: priorityBadges,
          columnGap: 4,
          margin: [0, 5, 0, 0]
        })
      }

      // Other choices or priority locations
      if (locations.length > 0) {
        cardContent.push({
          stack: [
            {
              text: metadata.groupByFacility ? 'Other Choices:' : 'Priority Locations:',
              style: 'otherChoicesLabel',
              margin: [0, 5, 0, 2]
            },
            { text: locations, style: 'otherChoicesText' }
          ]
        })
      }

      content.push({
        table: {
          widths: ['*'],
          body: [[{ stack: cardContent, margin: [5, 5, 5, 5] }]]
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => '#4DA4A4',
          vLineColor: () => '#4DA4A4',
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0
        },
        margin: [0, 0, 0, 5]
      })
    })
  })

  return {
    pageSize: 'LETTER',
    pageOrientation: 'portrait',
    pageMargins: [30, 50, 30, 50],

    header: (currentPage: number, pageCount: number) => {
      if (currentPage === 1) return null
      return {
        text: 'Prioritization Report',
        alignment: 'center',
        margin: [30, 15, 30, 10],
        fontSize: 9,
        color: '#666'
      }
    },

    footer: (currentPage: number, pageCount: number) => ({
      columns: [
        {
          text: `Generated: ${new Date().toLocaleDateString()}`,
          alignment: 'left',
          fontSize: 7,
          color: '#666',
          margin: [30, 0, 0, 0]
        },
        {
          text: `Page ${String(currentPage)} of ${String(pageCount)}`,
          alignment: 'right',
          fontSize: 7,
          color: '#666',
          margin: [0, 0, 30, 0]
        }
      ]
    }),

    content,

    styles: {
      title: {
        fontSize: 20,
        bold: true,
        color: '#333'
      },
      metadataLabel: {
        fontSize: 7,
        color: '#666',
        bold: true,
        margin: [0, 0, 0, 2]
      },
      metadataValue: {
        fontSize: 9,
        color: '#333',
        bold: true
      },
      facilityHeaderText: {
        fontSize: 9,
        bold: true
      },
      badge: {
        fontSize: 7,
        bold: true,
        color: '#333'
      },
      rankNumber: {
        fontSize: 8,
        bold: true,
        color: '#4DA4A4'
      },
      childName: {
        fontSize: 9,
        bold: true,
        color: '#333'
      },
      childDetails: {
        fontSize: 7,
        color: '#666'
      },
      metricLabel: {
        fontSize: 6,
        color: '#666'
      },
      metricValue: {
        fontSize: 8,
        bold: true,
        color: '#333'
      },
      priorityBadge: {
        fontSize: 7,
        bold: true
      },
      otherChoicesLabel: {
        fontSize: 6,
        bold: true,
        color: '#666'
      },
      otherChoicesText: {
        fontSize: 6,
        color: '#666'
      }
    }
  }
}
